import {
  AmbientLight,
  Color,
  DirectionalLight,
  Fog,
  Mesh,
  MeshStandardMaterial,
  OrthographicCamera,
  Plane,
  PlaneGeometry,
  Scene,
  SpotLight,
  Vector3,
  WebGPURenderer,
} from 'three/webgpu';

import { getDevPanel, persistDevPanel } from '../dev/devPanel';
import { installModelTransformFolder, type ModelTransformHandle } from '../dev/modelControls';
import { applyTransform, loadModel, type LoadedModel } from '../lib/modelLoader';
import { MODELS } from '../assets/models';
import { createBackground, DEFAULT_BACKGROUND } from './backgrounds';
import type { BackgroundContext, BackgroundId, BackgroundScene } from './backgrounds';
import type { SceneController, SectionId } from './controller';

export type QualityTier = 'mobile' | 'desktop';

export interface StudioSceneOptions {
  canvas: HTMLCanvasElement;
  quality?: QualityTier;
  reducedMotion?: boolean;
  /** Which candidate to mount. Non-default ids are DEV-only. */
  background?: BackgroundId;
  /**
   * Forwarded to the background so page layout can reserve the space it
   * occupies. Full-viewport candidates publish 0.
   */
  onLayoutChange?: (gridWidthPx: number) => void;
}

export function detectQuality(): QualityTier {
  if (typeof window === 'undefined') return 'desktop';
  const coarse = window.matchMedia('(pointer: coarse)').matches;
  const small = window.matchMedia('(max-width: 768px)').matches;
  return coarse || small ? 'mobile' : 'desktop';
}

interface StudioSettings {
  fov: number;
  cameraHeight: number;
  cameraDistance: number;
  cameraTargetY: number;
  parallaxStrength: number;
  fogDensity: number;
  ambientIntensity: number;
  keyColor: string;
  keyIntensity: number;
  keyAngle: number;
  keyPenumbra: number;
  keyHeight: number;
  keyOffsetX: number;
  keyOffsetZ: number;
  fillColor: string;
  fillIntensity: number;
  fillHeight: number;
  fillOffsetX: number;
  fillOffsetZ: number;
  floorColor: string;
  floorRoughness: number;
  floorMetalness: number;
  modelTestCube: boolean;
}

const DEFAULT_SETTINGS: StudioSettings = {
  fov: 42,
  cameraHeight: 2.4,
  cameraDistance: 7,
  cameraTargetY: 0.4,
  parallaxStrength: 0.35,
  fogDensity: 0.07,
  ambientIntensity: 0.06,
  keyColor: '#9be8ff',
  keyIntensity: 28,
  keyAngle: 0.85,
  keyPenumbra: 0.85,
  keyHeight: 4.2,
  keyOffsetX: -3.2,
  keyOffsetZ: 1.6,
  fillColor: '#5d4be8',
  fillIntensity: 0.6,
  fillHeight: 3.2,
  fillOffsetX: 3.4,
  fillOffsetZ: -1.4,
  floorColor: '#080a14',
  floorRoughness: 0.7,
  floorMetalness: 0.15,
  modelTestCube: false,
};

export class StudioScene implements SceneController {
  private renderer: WebGPURenderer | null = null;
  private readonly scene: Scene;
  private readonly camera: OrthographicCamera;
  private readonly settings: StudioSettings;
  private readonly maxPixelRatio: number;
  private readonly canvas: HTMLCanvasElement;
  private readonly quality: QualityTier;

  // 3D infrastructure (disabled for C.1, kept for future use)
  // private floor!: Mesh;
  // private floorMaterial!: MeshStandardMaterial;
  // private keyLight!: SpotLight;
  // private fillLight!: DirectionalLight;
  // private ambient!: AmbientLight;
  // private fog!: Fog;
  private background: BackgroundScene | null = null;
  private backgroundId: BackgroundId;
  private readonly backgroundContext: BackgroundContext;

  private readonly cameraTarget = new Vector3(0, 0, 0);
  private readonly pointerNorm = { x: 0, y: 0 };

  private resizeObserver: ResizeObserver | null = null;
  private pointerHandler: ((e: PointerEvent) => void) | null = null;
  private clickHandler: ((e: PointerEvent) => void) | null = null;
  private testCubeModel: LoadedModel | null = null;
  private testCubePlacement: import('three/webgpu').Group | null = null;
  private testCubeFolder: ReturnType<typeof getDevPanel> | null = null;
  private testCubeTransform: ModelTransformHandle | null = null;
  private running = false;
  private startTime = 0;
  private prevTime = -1;
  private readonly reducedMotion: boolean;
  private reducedMotionActiveUntil = 0;
  private guiCleanup: (() => void) | null = null;

  constructor({
    canvas,
    quality = detectQuality(),
    reducedMotion = false,
    background = DEFAULT_BACKGROUND,
    onLayoutChange,
  }: StudioSceneOptions) {
    this.canvas = canvas;
    this.quality = quality;
    this.reducedMotion = reducedMotion;
    this.maxPixelRatio = quality === 'mobile' ? 1.5 : 2;
    this.settings = { ...DEFAULT_SETTINGS };

    this.scene = new Scene();
    this.scene.background = new Color('#02030a');

    // Orthographic camera for 2D viewport rendering (no perspective distortion)
    this.camera = new OrthographicCamera(-1, 1, 1, -1, 0.1, 100);
    this.camera.position.z = 1;

    // 3D infrastructure disabled (C.1 scope)
    // this.buildLights();
    // this.buildFloor();

    this.backgroundId = background;
    this.backgroundContext = { quality, reducedMotion, onLayoutChange };
  }

  async init(): Promise<void> {
    this.renderer = new WebGPURenderer({
      canvas: this.canvas,
      antialias: this.quality === 'desktop',
      powerPreference: this.quality === 'mobile' ? 'low-power' : 'high-performance',
    });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, this.maxPixelRatio));
    await this.renderer.init();

    this.background = await this.mountBackground(this.renderer);
    this.scene.add(this.background.object);
    await this.background.init?.(this.renderer);

    this.handleResize();
    this.observeResize();
    this.setupClickHandler();
    this.attachPointerTracking();
    this.installDevPanel();
  }

  private async mountBackground(renderer: WebGPURenderer): Promise<BackgroundScene> {
    const candidate = await createBackground(this.backgroundId, this.backgroundContext);
    if (candidate.supported?.(renderer) === false) {
      // eslint-disable-next-line no-console
      console.warn(
        `[studio] background '${this.backgroundId}' needs the WebGPU backend; falling back to '${DEFAULT_BACKGROUND}'`,
      );
      candidate.dispose();
      this.backgroundId = DEFAULT_BACKGROUND;
      return createBackground(DEFAULT_BACKGROUND, this.backgroundContext);
    }
    return candidate;
  }

  start(): void {
    if (!this.renderer || this.running) return;
    this.running = true;
    if (this.startTime === 0) this.startTime = performance.now();
    if (this.reducedMotion) {
      const t = (performance.now() - this.startTime) / 1000;
      // Extend the active window so current burst (if any) completes, or render at least 3 frames
      this.reducedMotionActiveUntil = Math.max(this.reducedMotionActiveUntil, t + 0.05);
    }
    this.renderer.setAnimationLoop(this.tick);
  }

  stop(): void {
    if (!this.renderer) return;
    this.running = false;
    this.renderer.setAnimationLoop(null);
  }

  dispose(): void {
    this.stop();
    this.guiCleanup?.();
    this.resizeObserver?.disconnect();
    if (this.clickHandler) this.canvas.removeEventListener('pointerdown', this.clickHandler);
    if (this.pointerHandler) window.removeEventListener('pointermove', this.pointerHandler);
    this.unmountTestCube();
    this.background?.dispose();
    this.background = null;
    this.renderer?.dispose();
    this.renderer = null;
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  setActiveSection(_section: SectionId): void {}

  private tick = (): void => {
    if (!this.renderer || !this.background) return;
    const t = (performance.now() - this.startTime) / 1000;
    const dt = this.prevTime < 0 ? 0 : t - this.prevTime;
    this.prevTime = t;
    this.background.update(t, dt);
    this.background.compute?.(this.renderer);
    this.renderer.render(this.scene, this.camera);
    if (this.reducedMotion && t > this.reducedMotionActiveUntil) {
      this.stop();
    }
  };

  // 3D infrastructure methods (disabled for C.1, kept for future use)
  /*
  private buildLights(): void {
    const s = this.settings;
    this.ambient = new AmbientLight(0xffffff, s.ambientIntensity);
    this.scene.add(this.ambient);

    this.keyLight = new SpotLight(
      new Color(s.keyColor),
      s.keyIntensity,
      0,
      s.keyAngle,
      s.keyPenumbra,
      1.4,
    );
    this.keyLight.position.set(s.keyOffsetX, s.keyHeight, s.keyOffsetZ);
    this.keyLight.target.position.set(0, 0, 0);
    this.scene.add(this.keyLight);
    this.scene.add(this.keyLight.target);

    this.fillLight = new DirectionalLight(new Color(s.fillColor), s.fillIntensity);
    this.fillLight.position.set(s.fillOffsetX, s.fillHeight, s.fillOffsetZ);
    this.fillLight.target.position.set(0, 0, 0);
    this.scene.add(this.fillLight);
    this.scene.add(this.fillLight.target);
  }

  private buildFloor(): void {
    const s = this.settings;
    this.floorMaterial = new MeshStandardMaterial({
      color: new Color(s.floorColor),
      roughness: s.floorRoughness,
      metalness: s.floorMetalness,
    });
    this.floor = new Mesh(new PlaneGeometry(60, 60, 1, 1), this.floorMaterial);
    this.floor.rotation.x = -Math.PI / 2;
    this.floor.position.y = 0;
    this.scene.add(this.floor);
  }

  private applyCameraTransform(px: number, py: number, _t = 0): void {
    const s = this.settings;
    const parallax = s.parallaxStrength;
    const baseX = px * parallax;
    const baseY = s.cameraHeight + py * parallax * 0.4;
    const baseZ = s.cameraDistance;
    this.camera.position.set(baseX, baseY, baseZ);
    this.cameraTarget.set(0, s.cameraTargetY, 0);
    this.camera.lookAt(this.cameraTarget);
    if (this.camera.fov !== s.fov) {
      this.camera.fov = s.fov;
      this.camera.updateProjectionMatrix();
    }
  }
  */

  private observeResize(): void {
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', this.handleResize);
      return;
    }
    this.resizeObserver = new ResizeObserver(() => this.handleResize());
    this.resizeObserver.observe(this.canvas);
  }

  private handleResize = (): void => {
    if (!this.renderer) return;
    const w = window.innerWidth;
    const h = window.innerHeight;
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, this.maxPixelRatio));
    this.renderer.setSize(w, h, false);
    // Orthographic camera: update left/right/top/bottom based on viewport
    const aspect = w / Math.max(h, 1);
    this.camera.left = -aspect;
    this.camera.right = aspect;
    this.camera.top = 1;
    this.camera.bottom = -1;
    this.camera.updateProjectionMatrix();
    this.background?.setViewportSize(w, h);
  };

  private setupClickHandler(): void {
    this.clickHandler = (e: PointerEvent): void => {
      const rect = this.canvas.getBoundingClientRect();
      const normalizedX = (e.clientX - rect.left) / rect.width;
      const normalizedY = (e.clientY - rect.top) / rect.height;
      this.background?.onCanvasClick?.(normalizedX, normalizedY);
    };
    this.canvas.addEventListener('pointerdown', this.clickHandler, { passive: true });
  }

  /**
   * Listens on `window`, not the canvas — `#atmosphere` is `pointer-events:
   * none` and sits under `#app`, so canvas-scoped moves never fire.
   */
  private attachPointerTracking(): void {
    this.pointerHandler = (e: PointerEvent): void => {
      this.pointerNorm.x = e.clientX / window.innerWidth;
      this.pointerNorm.y = e.clientY / window.innerHeight;
      this.background?.onPointerMove?.(this.pointerNorm.x, this.pointerNorm.y);
    };
    window.addEventListener('pointermove', this.pointerHandler, { passive: true });
  }

  private installDevPanel(): void {
    const panel = getDevPanel();
    if (!panel) return;

    const folder = panel.addFolder('Studio');

    // 3D controls disabled (C.1 scope)
    /*
    const camera = folder.addFolder('Camera');
    camera.add(s, 'fov', 20, 90, 1).name('FoV');
    camera.add(s, 'cameraHeight', 0.5, 8, 0.05).name('Height');
    camera.add(s, 'cameraDistance', 2, 20, 0.1).name('Distance');
    camera.add(s, 'cameraTargetY', -1, 4, 0.05).name('Target Y');
    camera.add(s, 'parallaxStrength', 0, 1.5, 0.01).name('Parallax');
    ... [more 3D controls omitted for brevity] ...
    */

    const models = folder.addFolder('Models');
    models.add(this.settings, 'modelTestCube').name('test-cube').onChange((v: boolean) => {
      if (v) void this.mountTestCube(panel);
      else this.unmountTestCube();
    });
    models.close();

    const bg = panel.addFolder(`Background · ${this.backgroundId}`);
    this.background?.installDevPanel(bg);

    folder.close();

    persistDevPanel(this.quality);

    this.guiCleanup = (): void => {
      folder.destroy();
    };
  }

  // 3D methods disabled (C.1 scope)
  /*
  private placeKeyLight(): void {
    const s = this.settings;
    this.keyLight.position.set(s.keyOffsetX, s.keyHeight, s.keyOffsetZ);
    this.keyLight.target.position.set(0, 0, 0);
  }

  private placeFillLight(): void {
    const s = this.settings;
    this.fillLight.position.set(s.fillOffsetX, s.fillHeight, s.fillOffsetZ);
    this.fillLight.target.position.set(0, 0, 0);
  }
  */

  private async mountTestCube(panel: NonNullable<ReturnType<typeof getDevPanel>>): Promise<void> {
    if (this.testCubePlacement) return;
    try {
      const entry = MODELS['test-cube'];
      this.testCubeModel = await loadModel(entry);
      const placement = this.testCubeModel.source.clone(true) as import('three/webgpu').Group;
      applyTransform(placement, entry.defaults);
      this.scene.add(placement);
      this.testCubePlacement = placement;

      const folder = panel.addFolder('test-cube placement');
      this.testCubeTransform = installModelTransformFolder(folder, placement);
      folder.close();
      this.testCubeFolder = folder as unknown as ReturnType<typeof getDevPanel>;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[studio] test-cube mount failed:', err);
      this.settings.modelTestCube = false;
    }
  }

  private unmountTestCube(): void {
    this.testCubeTransform?.destroy();
    this.testCubeTransform = null;
    if (this.testCubeFolder) {
      (this.testCubeFolder as unknown as { destroy(): void }).destroy();
      this.testCubeFolder = null;
    }
    if (this.testCubePlacement) this.scene.remove(this.testCubePlacement);
    this.testCubePlacement = null;
    this.testCubeModel?.dispose();
    this.testCubeModel = null;
  }

  // 3D methods disabled (C.1 scope)
  /*
  private placeKeyLight(): void {
    const s = this.settings;
    this.keyLight.position.set(s.keyOffsetX, s.keyHeight, s.keyOffsetZ);
    this.keyLight.target.position.set(0, 0, 0);
  }

  private placeFillLight(): void {
    const s = this.settings;
    this.fillLight.position.set(s.fillOffsetX, s.fillHeight, s.fillOffsetZ);
    this.fillLight.target.position.set(0, 0, 0);
  }

  private applyFog(): void {
    const density = this.settings.fogDensity;
    if (density <= 0) {
      this.scene.fog = null;
      return;
    }
    this.fog.near = 4;
    this.fog.far = 4 + 26 * (1 - Math.min(density, 0.99));
    this.scene.fog = this.fog;
  }
  */
}
